'use client'
/**
 * QueryID Tracker Utility Functions
 *
 * Tracks Algolia queryIDs for products and cart items with 1-hour expiration.
 */

import type {
  QueryIdTrackerData,
  SerializedQueryIdTrackerData,
} from '@maestro/core/event/types'

import { createLogger } from '@maestro/core/logger'

const log = createLogger('maestro:core:queryid-tracker')

const QUERYID_TRACKER_KEY = 'queryIdTracker'
const ONE_HOUR_MS = 3600000 // 1 hour in milliseconds

const serializeQueryIdTrackerData = (
  data: QueryIdTrackerData
): SerializedQueryIdTrackerData => {
  return {
    products: Array.from(data.products.entries()),
    cartProducts: {
      cartId: data.cartProducts.cartId,
      products: Array.from(data.cartProducts.products.entries()),
    },
  }
}

const deserializeQueryIdTrackerData = (
  serialized: SerializedQueryIdTrackerData
): QueryIdTrackerData => {
  return {
    products: new Map(serialized.products),
    cartProducts: {
      cartId: serialized.cartProducts.cartId,
      products: new Map(serialized.cartProducts.products),
    },
  }
}

const removeExpiredQueryIdTrackerData = (
  data: QueryIdTrackerData
): QueryIdTrackerData => {
  const now = Date.now()

  for (const [productId, entry] of data.products) {
    if (now - entry.timestamp > ONE_HOUR_MS) {
      data.products.delete(productId)
    }
  }

  for (const [productId, entry] of data.cartProducts.products) {
    if (now - entry.timestamp > ONE_HOUR_MS) {
      data.cartProducts.products.delete(productId)
    }
  }

  return data
}

const getQueryIdTrackerData = (): QueryIdTrackerData => {
  if (typeof window === 'undefined') {
    return {
      products: new Map(),
      cartProducts: { cartId: '', products: new Map() },
    }
  }

  try {
    const data = localStorage.getItem(QUERYID_TRACKER_KEY)
    if (!data) {
      return {
        products: new Map(),
        cartProducts: { cartId: '', products: new Map() },
      }
    }

    const parsedData = JSON.parse(data)

    if (
      Array.isArray(parsedData.products) &&
      parsedData.cartProducts &&
      typeof parsedData.cartProducts.cartId === 'string' &&
      Array.isArray(parsedData.cartProducts.products)
    ) {
      return removeExpiredQueryIdTrackerData(
        deserializeQueryIdTrackerData(
          parsedData as SerializedQueryIdTrackerData
        )
      )
    }

    log.warn('[QueryID Tracker] Invalid data structure in localStorage')
    return {
      products: new Map(),
      cartProducts: { cartId: '', products: new Map() },
    }
  } catch (error) {
    log.error('[QueryID Tracker] Error reading from localStorage:', error)
    return {
      products: new Map(),
      cartProducts: { cartId: '', products: new Map() },
    }
  }
}

const saveQueryIdTrackerData = (data: QueryIdTrackerData): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const serialized = serializeQueryIdTrackerData(data)
    localStorage.setItem(QUERYID_TRACKER_KEY, JSON.stringify(serialized))
  } catch (error) {
    log.error('[QueryID Tracker] Error writing to localStorage:', error)
  }
}

export const addProductQueryId = (
  productId: string,
  queryID: string,
  listId?: string,
  listName?: string
): void => {
  const data = getQueryIdTrackerData()

  data.products.set(productId, {
    queryID,
    timestamp: Date.now(),
    listId,
    listName,
  })

  saveQueryIdTrackerData(data)
}

export const removeProductQueryId = (productId: string): void => {
  const data = getQueryIdTrackerData()
  if (data.products.has(productId)) {
    data.products.delete(productId)
    saveQueryIdTrackerData(data)
  }
}

export const getProductQueryId = (productId: string): string => {
  const data = getQueryIdTrackerData()
  const entry = data.products.get(productId)
  if (entry) {
    return entry.queryID
  }
  return ''
}

export const addProductToCartWithQueryId = (
  cartId: string,
  productId: string,
  queryID: string,
  listId?: string,
  listName?: string
): void => {
  const data = getQueryIdTrackerData()

  if (data.cartProducts.cartId !== cartId) {
    data.cartProducts.cartId = cartId
    data.cartProducts.products.clear()
  }

  data.cartProducts.products.set(productId, {
    queryID,
    timestamp: Date.now(),
    listId,
    listName,
  })
  saveQueryIdTrackerData(data)
}

export const removeProductWithQueryIdFromCart = (
  cartId: string,
  productId: string
): void => {
  const data = getQueryIdTrackerData()

  if (data.cartProducts.cartId !== cartId) {
    data.cartProducts.cartId = cartId
    data.cartProducts.products.clear()
    return
  }

  if (data.cartProducts.products.has(productId)) {
    data.cartProducts.products.delete(productId)
    saveQueryIdTrackerData(data)
  }
}

export const getProductQueryIdFromCart = (
  cartId: string,
  productId: string
): string => {
  const data = getQueryIdTrackerData()

  if (data.cartProducts.cartId === cartId) {
    const entry = data.cartProducts.products.get(productId)
    if (entry) {
      return entry.queryID
    }
  }
  return ''
}

export const getProductListInfo = (
  productId: string
): { listId?: string; listName?: string } => {
  const data = getQueryIdTrackerData()
  const entry = data.products.get(productId)
  if (entry) {
    return { listId: entry.listId, listName: entry.listName }
  }
  return {}
}

export const getProductListInfoFromCart = (
  cartId: string,
  productId: string
): { listId?: string; listName?: string } => {
  const data = getQueryIdTrackerData()

  if (data.cartProducts.cartId === cartId) {
    const entry = data.cartProducts.products.get(productId)
    if (entry) {
      return { listId: entry.listId, listName: entry.listName }
    }
  }
  return {}
}

export const updateQueryIdTrackerCartId = (newCartId: string): void => {
  const data = getQueryIdTrackerData()

  data.cartProducts.cartId = newCartId
  saveQueryIdTrackerData(data)
}

export const removeAllProductsWithQueryIdFromCart = (): void => {
  const data = getQueryIdTrackerData()

  data.cartProducts.products.clear()
  data.cartProducts.cartId = ''
  saveQueryIdTrackerData(data)
}

export const removeAllQueryIdTrackerData = (): void => {
  const emptyData: QueryIdTrackerData = {
    products: new Map(),
    cartProducts: { cartId: '', products: new Map() },
  }
  saveQueryIdTrackerData(emptyData)
}
